import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Input, Radio, Row } from 'antd';
import { Link } from 'react-router-dom';
import { ERoutesPrivate } from 'common/models/routesModel';
import { ECommonFormType } from 'common/models/commonModel';
import { formRules } from 'common/const/common.const';
import { useFormMapper } from 'common/helpers/form.helper';
import { useIsContentRestricted } from 'common/hooks/useIsContentRestricted';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { setUserModelError, setUserModelParams } from 'app/store/reducers/user.reducer';
import { EUserRole, EUserRoleText, EUserStatus, IUserInviteData, IUserUpdateData } from 'entities/User/User.models';
import { EStoreScopeType, IProfile } from 'entities/Permission/Permission.models';
import { ProfileSelector } from 'entities/Permission/components/Selector/ProfileSelector';
import { ProfilePreview } from 'entities/Permission/components/ProfilePreview';
import { StoreScopeTypeSelector } from 'entities/Permission/components/Selector/StoreScopeTypeSelector';
import { StoreScopeIdsSelector } from 'entities/Permission/components/Selector/StoreScopeIdsSelector';

interface IComponentProps {
  inviteUser: (data: IUserInviteData) => void;
  updateUser: (data: IUserUpdateData) => void;
  reInviteUser: () => void;
  blockUser: () => void;
  deleteUser: () => void;
  typeForm: ECommonFormType;
}

type AllProps = IComponentProps;

export const UserForm: React.FC<AllProps> = (props) => {
  const dispatch = useAppDispatch();
  const { userModel, userModelLoading, userModelParams, userModelError } = useAppSelector((state) => state.user);
  const { typeForm, inviteUser, updateUser, reInviteUser, blockUser, deleteUser } = props;
  // A marketing-profile franchisor can view user records but not mutate them — only Cancel stays,
  // and every field is locked read-only.
  const canManageUsers = !useIsContentRestricted();
  const isFieldDisabled = userModelLoading || !canManageUsers;
  const [form] = Form.useForm();
  const [userRole, setUserRole] = useState<string>();
  // ponytail: profile + store scope all live in the Form's own field store now (not local state) —
  // wrapping each in its own Form.Item is what lets them show inline validation errors.
  const [selectedProfile, setSelectedProfile] = useState<IProfile | undefined>();
  const isUpdateForm = typeForm === ECommonFormType.Update;
  // `storeScope` arrives nested ({ type, ids }); flatten it so `useFormMapper` can prefill the two
  // separate "storeScopeType" / "storeScopeIds" fields the same way it prefills firstName etc.
  const mapperData = useMemo(
    () =>
      userModel && {
        ...userModel,
        storeScopeType: userModel.storeScope?.type ?? undefined,
        storeScopeIds: userModel.storeScope?.ids ?? [],
      },
    [userModel]
  );
  const { fields } = useFormMapper(
    ['firstName', 'lastName', 'phone', 'email', 'profile', 'storeScopeType', 'storeScopeIds'],
    mapperData,
    userModelParams?.data,
    userModelError
  );

  const isPending = userModel?.status === EUserStatus.Pending;
  const isBlocked = userModel?.status === EUserStatus.Blocked;
  const isFranchisor = (!isUpdateForm && userRole === EUserRole.Franchisor) || userModel?.role === EUserRole.Franchisor;
  const blockButtonText = isPending ? 'Revoke invitation' : isBlocked ? 'Unblock' : 'Block';
  const submitButtonText = isUpdateForm ? 'Save' : 'Invite';
  const stores = userModel?.stores;

  // MBC-84 Phase 2: profile + store scope are mandatory for a franchisor.
  // - create: always required;
  // - edit: required only when the user already has a profile (a legacy profile-less franchisor
  //   may still be edited without one, but an assigned profile can never be reset back to empty).
  const isProfileRequired = isUpdateForm ? !!userModel?.profile : true;

  const applyFranchisorScope = (
    vals: IUserInviteData | IUserUpdateData,
    profileId?: string,
    scopeType?: EStoreScopeType,
    scopeIds?: string[]
  ) => {
    // ProfileSelector is string-based; BE contract narrows to the profile id union.
    vals.profile = profileId as IUserInviteData['profile'];
    vals.storeScopeType = scopeType;
    // "All" carries no ids; the other scope types persist the picked region/group/store ids.
    vals.storeScopeIds = scopeType && scopeType !== EStoreScopeType.All ? scopeIds : [];
  };

  const addUser = (values: any) => {
    const { email, role, firstName, lastName, phone, profile, storeScopeType, storeScopeIds } = values;
    const vals: IUserInviteData = {
      email,
      role,
      firstName,
      lastName,
      phone,
    };

    if (userRole === EUserRole.Franchisor) {
      applyFranchisorScope(vals, profile, storeScopeType, storeScopeIds);
    }

    inviteUser(vals);
  };

  const editUser = (values: any) => {
    const { firstName, lastName, phone, profile, storeScopeType, storeScopeIds } = values;
    const vals: IUserUpdateData = {
      firstName,
      lastName,
      phone,
    };

    if (userModel?.role === EUserRole.Franchisor) {
      applyFranchisorScope(vals, profile, storeScopeType, storeScopeIds);
    }

    updateUser(vals);
  };

  useEffect(() => {
    return () => {
      dispatch(setUserModelParams(null));
      dispatch(setUserModelError(null));
    };
  }, []);

  return (
    <Form form={form} onFinish={isUpdateForm ? editUser : addUser} fields={fields} layout="vertical">
      {!isUpdateForm && (
        <>
          <span className="basic__text_title" title="Who do you want to invite?">
            Who do you want to invite?
          </span>
          <Form.Item
            rules={[
              {
                required: true,
                message: 'Role is required',
              },
            ]}
            name="role"
            initialValue={EUserRole.Franchisee}
            className="mt-5"
          >
            {/* MBC-84 Phase 2: SalesOps role is deprecated — a Sales Operations franchisor
                profile replaces it (BE also rejects role=salesop on create). */}
            <Radio.Group onChange={(e) => setUserRole(e.target.value)} disabled={isFieldDisabled}>
              <Radio value={EUserRole.Franchisee}>{EUserRoleText.Franchisee}</Radio>
              <Radio value={EUserRole.Franchisor}>{EUserRoleText.Franchisor}</Radio>
            </Radio.Group>
          </Form.Item>
        </>
      )}
      <Row className="mb-5" align="middle">
        <span className="basic__text_title" title="Personal info">
          Personal info
        </span>
      </Row>
      <Row gutter={[28, 10]}>
        <Col span={12}>
          <Form.Item
            rules={[
              {
                required: true,
                message: 'First name is required',
              },
              {
                min: 2,
                message: 'First name must be longer than or equal 2 characters',
              },
              {
                max: 32,
                message: 'First name must be shorter than or equal 32 characters',
              },
            ]}
            name="firstName"
            label="First name"
          >
            <Input name="firstName" type="text" placeholder="First name" disabled={isFieldDisabled} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            rules={[
              {
                required: true,
                message: 'Last name is required',
              },
              {
                min: 2,
                message: 'Last name must be longer than or equal 2 characters',
              },
              {
                max: 32,
                message: 'Last name must be shorter than or equal 32 characters',
              },
            ]}
            name="lastName"
            label="Last name"
          >
            <Input name="lastName" type="text" placeholder="Last name" disabled={isFieldDisabled} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[28, 10]}>
        <Col span={12}>
          <Form.Item
            rules={[
              {
                required: true,
                message: 'Phone is required',
              },
              formRules.correctPhone,
            ]}
            name="phone"
            label="Phone number"
          >
            <Input name="Phone" type="tel" placeholder="+63 9XX XXX XXXX" disabled={isFieldDisabled} />
          </Form.Item>
        </Col>
      </Row>
      {isFranchisor && (
        <>
          <Row className="mt-8 mb-5" align="middle">
            <span className="basic__text_title" title="Position & access">
              Position &amp; access
            </span>
          </Row>
          <Row gutter={[28, 10]}>
            <Col span={12}>
              <Form.Item
                name="profile"
                label="Position"
                rules={
                  isProfileRequired
                    ? [{ required: true, message: 'Position (profile) is required for a franchisor' }]
                    : []
                }
              >
                <ProfileSelector
                  disabled={isFieldDisabled}
                  onChange={(_id, profile) => setSelectedProfile(profile)}
                  onResolve={setSelectedProfile}
                />
              </Form.Item>
            </Col>
          </Row>
          {selectedProfile && (
            <Row className="mt-5" gutter={[28, 10]}>
              <Col span={12}>
                <div className="mb-2 fs-xxs font-family__default">Module access (read-only preview)</div>
                <ProfilePreview profile={selectedProfile} />
              </Col>
            </Row>
          )}
          <Row className="mt-5 mb-5" align="middle">
            <span className="basic__text_title" title="Store scope">
              Store scope
            </span>
          </Row>
          <Row gutter={[28, 10]}>
            <Col span={12}>
              {/* Whether scope-type is required depends on the live "profile" value, not just isProfileRequired —
                  assigning a profile in this session makes a scope mandatory even on a legacy edit. */}
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.profile !== cur.profile}>
                {({ getFieldValue }) => {
                  const isScopeRequired = isProfileRequired || !!getFieldValue('profile');

                  return (
                    <Form.Item
                      name="storeScopeType"
                      rules={
                        isScopeRequired ? [{ required: true, message: 'Store scope is required for a franchisor' }] : []
                      }
                    >
                      <StoreScopeTypeSelector
                        disabled={isFieldDisabled}
                        onChange={() => form.setFieldValue('storeScopeIds', [])}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.storeScopeType !== cur.storeScopeType}>
                {({ getFieldValue }) => {
                  const scopeType = getFieldValue('storeScopeType');

                  if (!scopeType || scopeType === EStoreScopeType.All) {
                    return null;
                  }

                  return (
                    <Form.Item
                      name="storeScopeIds"
                      rules={[{ required: true, message: 'Select at least one item for the chosen store scope' }]}
                    >
                      <StoreScopeIdsSelector type={scopeType} disabled={isFieldDisabled} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      {isUpdateForm && !!stores?.length && (
        <>
          <span className="basic__text_title" title="Stores">
            Stores
          </span>
          <Row align="middle">
            {stores.map((store, index) => (
              <li key={store.id} className="mr-2">
                <Link to={`${ERoutesPrivate.Stores}/${store.shortStoreId}`} className="basic__link">
                  {store.storeName}
                </Link>
                {index !== stores.length - 1 && ', '}
              </li>
            ))}
          </Row>
        </>
      )}

      {isUpdateForm ? (
        <>
          {isPending && (
            <>
              <Col span={24} className="mt-5">
                <span className="basic__text_title" title="Invitation">
                  Invitation
                </span>
              </Col>
              <Col span={24} className="mt-5 mb-5">
                <span
                  className="fs-xxs font-family__default"
                  title="The invitation to join the system will be sent to the specified email address."
                >
                  The invitation to join the system will be sent to the specified email address.
                </span>
              </Col>
            </>
          )}
          <Row className="mb-8 mt-5" gutter={[28, 0]}>
            <Col className="pb-2" span={24}>
              <span className="fs-xxs font-family__default" title="Email">
                Email
              </span>
            </Col>
            <Col span={12}>
              <Input
                type="text"
                name="email"
                placeholder="Email"
                disabled
                value={userModel?.email}
                title={userModel?.email}
              />
            </Col>
            {isPending && (
              <Col span={12}>
                <Button
                  onClick={reInviteUser}
                  disabled={userModelLoading}
                  loading={userModelLoading}
                  title="Resend invitation"
                >
                  Resend invitation
                </Button>
              </Col>
            )}
          </Row>
        </>
      ) : (
        <Row gutter={[28, 12]}>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: 'Email is required',
                },
              ]}
              name="email"
              label="Email"
            >
              <Input type="email" name="email" placeholder="Email" disabled={isFieldDisabled} />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row justify="space-between" align="middle">
        <Col span={16}>
          <Form.Item className="mb-0">
            <Button
              className="mr-8"
              htmlType="submit"
              type="primary"
              disabled={userModelLoading || !canManageUsers}
              loading={userModelLoading}
              title={submitButtonText}
            >
              {submitButtonText}
            </Button>
            <Link to={ERoutesPrivate.Users}>
              <Button disabled={userModelLoading} loading={userModelLoading} title="Cancel">
                Cancel
              </Button>
            </Link>
          </Form.Item>
        </Col>
        {isUpdateForm && (
          <Button
            danger
            disabled={userModelLoading || !canManageUsers}
            loading={userModelLoading}
            title={blockButtonText}
            onClick={isPending ? deleteUser : blockUser}
          >
            {blockButtonText}
          </Button>
        )}
      </Row>
    </Form>
  );
};
